import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { listWasteEvents, postWaste, reportWaste } from '../../api/inventoryOps';
import { listItems, listUoms } from '../../api/masterData';
import { listWarehouses } from '../../api/orgData';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  BusinessText,
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Pagination,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { DocumentStatus, Item, Uom, Warehouse, WasteEvent, WasteReasonCode } from '../../types/domain';

const WASTE_STATUSES: DocumentStatus[] = ['DRAFT', 'POSTED', 'CANCELLED'];

const REASON_CODES: WasteReasonCode[] = ['EXPIRED', 'DAMAGED', 'SPOILED', 'PREP_ERROR', 'OTHER'];

/**
 * Waste — report a DRAFT waste event (fn_report_waste) then post it
 * (fn_post_waste_checked), which writes a WASTE movement to stock_ledger
 * and rejects the post if there isn't enough stock. Migration 0033,
 * permission-gated + branch-scoped. Posting against a specific expiry lot
 * is not exposed in this screen yet — that needs the Expiry/Lot picker
 * wired to a specific waste event's item, tracked as follow-up.
 */
export default function WastePage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [events, setEvents] = useState<WasteEvent[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [postingId, setPostingId] = useState<string | null>(null);
  const [selectedWaste, setSelectedWaste] = useState<WasteEvent | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const canCreate = hasPermission('waste', 'create');
  const canPost = hasPermission('waste', 'post');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventRows, itemRows, uomRows, whRows] = await Promise.all([
        listWasteEvents(activeBranchId ?? undefined),
        listItems(),
        listUoms(),
        listWarehouses(activeBranchId ?? undefined),
      ]);
      setEvents(eventRows);
      setItems(itemRows);
      setUoms(uomRows);
      setWarehouses(whRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const handlePost = async (wasteId: string) => {
    setActionError(null);
    setPostingId(wasteId);
    try {
      await postWaste(wasteId);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setPostingId(null);
    }
  };

  const itemLabel = (id: string) => {
    const it = items.find((i) => i.item_id === id);
    return it ? `${it.item_code} — ${it.name}` : id;
  };
  const uomLabel = (id: string) => uoms.find((u) => u.uom_id === id)?.code ?? id;
  const warehouseName = (id: string) => warehouses.find((w) => w.warehouse_id === id)?.name ?? id;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((w) => {
      if (statusFilter !== 'ALL' && w.status !== statusFilter) return false;
      if (q && !w.waste_number.toLowerCase().includes(q) && !itemLabel(w.item_id).toLowerCase().includes(q)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, search, statusFilter, items]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);
  const wasteCounts = statusCounts(events, WASTE_STATUSES);

  return (
    <div>
      <PageHeader title="Waste" subtitle="Laporan barang rusak/kadaluarsa — posting menulis WASTE ke Stock Ledger. Migration 0010/0033." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId || warehouses.length === 0}>
          {showForm ? 'Batal' : '+ Laporkan waste'}
        </Button>
      )}
      {showForm && canCreate && activeBranchId && (
        <NewWasteForm
          items={items}
          uoms={uoms}
          warehouses={warehouses}
          branchId={activeBranchId}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {actionError && <ErrorNote text={actionError} />}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && events.length > 0 && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Laporan', value: events.length },
              { label: 'Draft', value: wasteCounts.DRAFT, tone: wasteCounts.DRAFT > 0 ? 'warning' : 'success' },
              { label: 'Posted', value: wasteCounts.POSTED, tone: 'success' },
              { label: 'Cancelled', value: wasteCounts.CANCELLED, tone: 'neutral' },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari no. waste atau item..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua status</option>
                <option value="DRAFT">Draft</option>
                <option value="POSTED">Posted</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
            }
          />
        </>
      )}
      {!loading && !error && events.length === 0 && <EmptyState text="Belum ada laporan waste." />}
      {!loading && !error && events.length > 0 && total === 0 && <EmptyState text="Tidak ada laporan yang cocok dengan pencarian/filter." />}
      {total > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>No. Waste</th>
              <th style={thStyle}>Qty</th>
              <th style={thStyle}>Alasan</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((w) => (
              <ClickableRow key={w.waste_id} onClick={() => setSelectedWaste(w)}>
                <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{w.waste_number}</td>
                <td style={tdStyle}>{w.qty}</td>
                <td style={tdStyle}>{w.reason_code}</td>
                <td style={tdStyle}>
                  <StatusBadge status={w.status} />
                </td>
                <td style={tdStyle}>
                  {canPost && w.status === 'DRAFT' && (
                    <Button
                      variant="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handlePost(w.waste_id);
                      }}
                      loading={postingId === w.waste_id}
                    >
                      Post ke Stock Ledger
                    </Button>
                  )}
                </td>
              </ClickableRow>
            ))}
          </tbody>
        </table>
      )}
      <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />

      <Drawer open={!!selectedWaste} onClose={() => setSelectedWaste(null)} title={selectedWaste ? `Waste ${selectedWaste.waste_number}` : 'Detail Waste'}>
        {selectedWaste && (
          <div>
            <DrawerField label="No. Waste" value={selectedWaste.waste_number} />
            <DrawerField label="Status" value={<StatusBadge status={selectedWaste.status} />} />
            <DrawerField label="Item" value={itemLabel(selectedWaste.item_id)} />
            <DrawerField label="Qty" value={`${selectedWaste.qty} ${uomLabel(selectedWaste.uom_id)}`} />
            <DrawerField label="Gudang" value={warehouseName(selectedWaste.warehouse_id)} />
            <DrawerField label="Alasan" value={selectedWaste.reason_code} />
            <DrawerField label="Catatan" value={<BusinessText text={selectedWaste.reason_note} />} />
            <DrawerField label="Diposting" value={selectedWaste.posted_at ? formatDateTime(selectedWaste.posted_at) : 'Belum diposting'} />
          </div>
        )}
      </Drawer>
    </div>
  );
}

function NewWasteForm({
  items,
  uoms,
  warehouses,
  branchId,
  onCreated,
}: {
  items: Item[];
  uoms: Uom[];
  warehouses: Warehouse[];
  branchId: string;
  onCreated: () => void;
}) {
  const { context } = usePermissions();
  const [warehouseId, setWarehouseId] = useState('');
  const [itemId, setItemId] = useState('');
  const [uomId, setUomId] = useState('');
  const [qty, setQty] = useState('');
  const [reasonCode, setReasonCode] = useState<WasteReasonCode>('DAMAGED');
  const [reasonNote, setReasonNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!context || !context.authenticated) return;
    setSubmitting(true);
    setError(null);
    try {
      await reportWaste({
        tenantId: context.tenant_id,
        branchId,
        warehouseId,
        itemId,
        uomId,
        qty: Number(qty),
        reasonCode,
        reasonNote: reasonNote || null,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 20, padding: 16 }}>
      <div style={{ width: 180 }}>
        <FieldLabel label="Gudang">
          <Select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {warehouses.map((w) => (
              <option key={w.warehouse_id} value={w.warehouse_id}>
                {w.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 220 }}>
        <FieldLabel label="Item">
          <Select value={itemId} onChange={(e) => setItemId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {items.map((it) => (
              <option key={it.item_id} value={it.item_id}>
                {it.item_code} — {it.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 100 }}>
        <FieldLabel label="UOM">
          <Select value={uomId} onChange={(e) => setUomId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {uoms.map((u) => (
              <option key={u.uom_id} value={u.uom_id}>
                {u.code}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 100 }}>
        <FieldLabel label="Qty">
          <TextInput type="number" min="0.01" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Alasan">
          <Select value={reasonCode} onChange={(e) => setReasonCode(e.target.value as WasteReasonCode)} required>
            {REASON_CODES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 220 }}>
        <FieldLabel label="Catatan (opsional)">
          <TextInput value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Laporkan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
